/**
 * Modulo M5: test finale. Regole pure e schemi.
 *
 * - Si apre alla chiusura della diretta (fineEffettiva) per `testFinestraOre` ore.
 * - Solo chi ha presenza >= soglia (il 75,0% esatto basta).
 * - Un solo tentativo, `testDurataMin` minuti con scadenza fissata dal server; le risposte si
 *   salvano una per una; consegna o scadenza chiudono. Oltre la scadenza (+ tolleranza) rifiuto.
 * - Superato con almeno `testCorretteMinime` risposte esatte (default 4 su 5).
 * - Ordine di domande e risposte mescolato per tentativo con un seme riproducibile.
 */
import { z } from 'zod';

export const REGOLE_TEST = {
  domandePerTest: 5,
  risposteMinimePerDomanda: 2,
  risposteMassimePerDomanda: 6,
  corretteMinimeDefault: 4,
  durataMinDefault: 10,
  finestraOreDefault: 2,
  /** secondi di tolleranza di rete oltre la scadenza */
  tolleranzaSecondi: 5,
  /** il timer diventa `warning` sotto questi minuti */
  avvisoUltimiMinuti: 2,
} as const;

export const ESITI_TENTATIVO = ['in_corso', 'superato', 'non_superato', 'scaduto'] as const;
export type EsitoTentativo = (typeof ESITI_TENTATIVO)[number];

// ---------------------------------------------------------------- Regole pure

/** Generatore pseudo-casuale deterministico (mulberry32) da un seme testuale. */
export function generatoreDaSeme(seme: string): () => number {
  let h = 1779033703 ^ seme.length;
  for (let i = 0; i < seme.length; i++) {
    h = Math.imul(h ^ seme.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Permutazione riproducibile: stesso seme, stesso ordine. */
export function mescola<T>(elementi: readonly T[], seme: string): T[] {
  const r = generatoreDaSeme(seme);
  const out = [...elementi];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export interface DomandaValutabile {
  id: string;
  rispostaCorrettaId: string;
}

export function valutaTentativo(domande: DomandaValutabile[], risposte: Record<string, string | undefined>, corretteMinime: number): { corrette: number; esito: 'superato' | 'non_superato' } {
  const corrette = domande.filter((d) => risposte[d.id] === d.rispostaCorrettaId).length;
  return { corrette, esito: corrette >= corretteMinime ? 'superato' : 'non_superato' };
}

export type MotivoNonDisponibile = 'diretta_non_chiusa' | 'presenza_insufficiente' | 'finestra_chiusa' | 'test_non_pronto';

export interface ParametriDisponibilita {
  statoWebinar: string;
  fineEffettiva: Date | null;
  testFinestraOre: number;
  percentualePresenza: number | null;
  sogliaPct: number;
  numeroDomande: number;
  adesso?: Date;
}

export interface Disponibilita {
  disponibile: boolean;
  motivo: MotivoNonDisponibile | null;
  apertoDa: Date | null;
  chiusoA: Date | null;
}

export function disponibilitaTest(p: ParametriDisponibilita): Disponibilita {
  const adesso = p.adesso ?? new Date();
  const apertoDa = p.fineEffettiva;
  const chiusoA = p.fineEffettiva ? new Date(p.fineEffettiva.getTime() + p.testFinestraOre * 3600_000) : null;
  if (!p.fineEffettiva || p.statoWebinar === 'in_diretta' || p.statoWebinar === 'pubblicato' || p.statoWebinar === 'bozza') {
    return { disponibile: false, motivo: 'diretta_non_chiusa', apertoDa, chiusoA };
  }
  if (p.numeroDomande < 1) return { disponibile: false, motivo: 'test_non_pronto', apertoDa, chiusoA };
  if (chiusoA && adesso > chiusoA) return { disponibile: false, motivo: 'finestra_chiusa', apertoDa, chiusoA };
  if (p.percentualePresenza === null || p.percentualePresenza < p.sogliaPct) return { disponibile: false, motivo: 'presenza_insufficiente', apertoDa, chiusoA };
  return { disponibile: true, motivo: null, apertoDa, chiusoA };
}

/** Un tentativo è ancora accettabile se non è scaduto, con la tolleranza di rete. */
export function tentativoAccettabile(scadenzaAt: Date, adesso = new Date()): boolean {
  return adesso.getTime() <= scadenzaAt.getTime() + REGOLE_TEST.tolleranzaSecondi * 1000;
}

// ---------------------------------------------------------------- Schemi API
const id = z.string().uuid();
const dataIso = z.string().datetime({ offset: true });

/** Admin: una domanda con le sue risposte; `corretta` esattamente una. */
export const domandaInputSchema = z
  .object({
    testo: z.string().trim().min(5, 'Scrivi la domanda per esteso.').max(1000),
    risposte: z
      .array(z.object({ testo: z.string().trim().min(1, 'Scrivi il testo della risposta.').max(500), corretta: z.boolean() }))
      .min(REGOLE_TEST.risposteMinimePerDomanda, `Servono almeno ${REGOLE_TEST.risposteMinimePerDomanda} risposte.`)
      .max(REGOLE_TEST.risposteMassimePerDomanda),
  })
  .refine((d) => d.risposte.filter((r) => r.corretta).length === 1, { message: 'Segna esattamente una risposta corretta.', path: ['risposte'] });
export type DomandaInput = z.infer<typeof domandaInputSchema>;

export const domandeTestInputSchema = z.object({
  domande: z.array(domandaInputSchema).max(20),
  corretteMinime: z.number().int().min(1).max(20).default(REGOLE_TEST.corretteMinimeDefault),
});
export type DomandeTestInput = z.infer<typeof domandeTestInputSchema>;

export const domandaTestAdminSchema = z.object({
  id,
  ordine: z.number().int(),
  testo: z.string(),
  risposte: z.array(z.object({ id, testo: z.string(), corretta: z.boolean() })),
});
export const testAdminSchema = z.object({
  domande: z.array(domandaTestAdminSchema),
  corretteMinime: z.number().int(),
  durataMin: z.number().int(),
  finestraOre: z.number().int(),
  /** vero se le domande sono modificabili (prima della chiusura della diretta) */
  modificabile: z.boolean(),
  pronto: z.boolean(),
});
export type TestAdmin = z.infer<typeof testAdminSchema>;

/** Partecipante: stato del test per questo webinar. */
export const statoTestSchema = z.object({
  disponibile: z.boolean(),
  motivo: z.enum(['diretta_non_chiusa', 'presenza_insufficiente', 'finestra_chiusa', 'test_non_pronto']).nullable(),
  apertoDa: dataIso.nullable(),
  chiusoA: dataIso.nullable(),
  percentualePresenza: z.number().nullable(),
  sogliaPct: z.number().int(),
  durataMin: z.number().int(),
  numeroDomande: z.number().int(),
  corretteMinime: z.number().int(),
  /** tentativo esistente (in corso o concluso) */
  tentativo: z
    .object({
      id,
      esito: z.enum(ESITI_TENTATIVO),
      iniziatoAt: dataIso,
      scadenzaAt: dataIso,
      consegnatoAt: dataIso.nullable(),
      corrette: z.number().int().nullable(),
      risposteDate: z.number().int(),
    })
    .nullable(),
  oraServer: dataIso,
});
export type StatoTest = z.infer<typeof statoTestSchema>;

/** Domanda come la vede chi fa il test: mai la risposta corretta. */
export const domandaTentativoSchema = z.object({
  id,
  posizione: z.number().int(),
  testo: z.string(),
  risposte: z.array(z.object({ id, testo: z.string() })),
  rispostaData: id.nullable(),
});
export const tentativoSchema = z.object({
  id,
  esito: z.enum(ESITI_TENTATIVO),
  iniziatoAt: dataIso,
  scadenzaAt: dataIso,
  oraServer: dataIso,
  domande: z.array(domandaTentativoSchema),
  corretteMinime: z.number().int(),
});
export type Tentativo = z.infer<typeof tentativoSchema>;

export const rispostaInputSchema = z.object({ domandaId: id, rispostaId: id });

export const esitoTestSchema = z.object({
  esito: z.enum(['superato', 'non_superato', 'scaduto']),
  corrette: z.number().int(),
  totale: z.number().int(),
  corretteMinime: z.number().int(),
  consegnatoAt: dataIso,
});
export type EsitoTest = z.infer<typeof esitoTestSchema>;

/** Admin: esiti per iscritto. */
export const esitoIscrittoSchema = z.object({
  userId: id,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  percentualePresenza: z.number().nullable(),
  ammesso: z.boolean(),
  tentativo: z
    .object({ id, esito: z.enum(ESITI_TENTATIVO), iniziatoAt: dataIso, consegnatoAt: dataIso.nullable(), corrette: z.number().int().nullable() })
    .nullable(),
});
export type EsitoIscritto = z.infer<typeof esitoIscrittoSchema>;
export const riepilogoTestSchema = z.object({
  ammessi: z.number().int(),
  iniziati: z.number().int(),
  superati: z.number().int(),
  nonSuperati: z.number().int(),
  scaduti: z.number().int(),
  finestraChiusaA: dataIso.nullable(),
});
